/**
 * Defensive parser for the POST /api/v3/users.json registration response.
 *
 * The backend returns several shapes:
 *  - `{ error: string, errors: { field: [messages] } }` for known validation
 *    failures (email/phone taken)
 *  - Rails-style `{ errors: { field: [messages] } }` (no top-level `error`)
 *    for other 422 validation errors
 *  - `{ field_errors: { field: [messages] } }` — the P01 backend's
 *    field-error shape, matching GraphQL's `extensions.field_errors`
 *    (TA-10140)
 *  - `{ user: { api_tokens: [{ token }] } }` on success
 *
 * Any other shape — including a non-JSON body parsed to null — must resolve
 * to a readable error instead of crashing the signup flow on
 * `response.user.api_tokens[0].token` (TA-10086 / FRONTEND-1XJ).
 */

export type RegisterSuccess = {
  status: 'success';
  authToken: string;
};

export type RegisterFailure = {
  status: 'error';
  /** Best user-readable message derived from the body, if any */
  message: string | null;
  /** Readable per-field messages from a Rails-style errors object */
  fieldErrors: Record<string, string>;
  /**
   * TA-10140: the same per-field messages, unhumanized and un-truncated, keyed
   * by the backend's own field name. Callers that map backend fields onto form
   * inputs (see `registerFormFieldErrors.ts`) need the raw message to branch on
   * — e.g. "has already been taken" — instead of string-matching the humanized
   * sentence, which changes whenever backend copy does.
   */
  fieldErrorsRaw: Record<string, string[]>;
};

export type RegisterParseResult = RegisterSuccess | RegisterFailure;

/** "email" + "is invalid" → "Email is invalid"; already-capitalized messages pass through */
const humanizeFieldError = (field: string, message: string): string => {
  if (!/^[a-z]/.test(message)) return message;
  const label = field.replace(/_/g, ' ');
  return `${label.charAt(0).toUpperCase()}${label.slice(1)} ${message}`;
};

export const parseRegisterResponse = (
  ok: boolean,
  body: unknown,
  options?: { status?: number; rateLimitMessage?: string }
): RegisterParseResult => {
  // TA-10842: signup sits behind the same throttle family as login. A 429 is
  // frequently bodyless, so without this guard `body` parses to null, no field
  // errors are found, and the caller either shows a misleading "network error"
  // or (where it maps only to fields) nothing at all. Surface the shared
  // rate-limit copy instead.
  if (options?.status === 429) {
    return {
      status: 'error',
      message: options.rateLimitMessage ?? null,
      fieldErrors: {},
      fieldErrorsRaw: {},
    };
  }

  const parsed =
    typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)
      : {};

  const topLevelError =
    typeof parsed.error === 'string' && parsed.error ? parsed.error : null;

  const toMessages = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string');
    }
    return typeof value === 'string' ? [value] : [];
  };

  const fieldErrors: Record<string, string> = {};
  const fieldErrorsRaw: Record<string, string[]> = {};
  let firstListError: string | null = null;

  const collectFieldErrors = (value: unknown) => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return;
    }
    Object.entries(value).forEach(([field, fieldValue]) => {
      const messages = toMessages(fieldValue);
      if (messages.length === 0) return;
      fieldErrorsRaw[field] = [...(fieldErrorsRaw[field] ?? []), ...messages];
      fieldErrors[field] = humanizeFieldError(field, fieldErrorsRaw[field][0]);
    });
  };

  if (Array.isArray(parsed.errors)) {
    firstListError =
      parsed.errors.find((item): item is string => typeof item === 'string') ??
      null;
  } else {
    collectFieldErrors(parsed.errors);
  }
  // TA-10140: the P01 backend returns validation failures under
  // `field_errors` (same key GraphQL uses in `extensions.field_errors`).
  // Read both until the REST endpoint's rollout is confirmed everywhere.
  collectFieldErrors(parsed.field_errors);

  const user = parsed.user as
    | { api_tokens?: { token?: unknown }[] }
    | undefined;
  const authToken = user?.api_tokens?.[0]?.token;
  if (ok && !topLevelError && typeof authToken === 'string' && authToken) {
    return { status: 'success', authToken };
  }

  const message =
    topLevelError ?? Object.values(fieldErrors)[0] ?? firstListError ?? null;
  return { status: 'error', message, fieldErrors, fieldErrorsRaw };
};
